import { useEffect, useState, useCallback } from "react";

/**
 * usePwaInstall — exposes install prompt state and iOS instructions.
 *
 * Returns:
 *   installed       : running as installed PWA (standalone)
 *   canInstall      : browser has fired beforeinstallprompt (Android/Chrome/Edge/Brave)
 *   isIos           : device is iOS Safari (needs Add-to-Home-Screen manual step)
 *   promptInstall() : trigger native install prompt (no-op on iOS)
 */
export default function usePwaInstall() {
  const [canInstall, setCanInstall] = useState(!!window.__megaInstallPrompt);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect standalone (installed) mode
    const mql = window.matchMedia?.("(display-mode: standalone)");
    const isStandalone = mql?.matches || window.navigator.standalone === true;
    setInstalled(!!isStandalone);

    const onAvailable = () => setCanInstall(true);
    const onInstalled = () => { setCanInstall(false); setInstalled(true); };
    window.addEventListener("mega-install-available", onAvailable);
    window.addEventListener("mega-installed", onInstalled);

    // In case the SW registered/prompt fired before this hook mounted
    if (window.__megaInstallPrompt) setCanInstall(true);

    return () => {
      window.removeEventListener("mega-install-available", onAvailable);
      window.removeEventListener("mega-installed", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const p = window.__megaInstallPrompt;
    if (!p) return { outcome: "unavailable" };
    try {
      p.prompt();
      const choice = await p.userChoice;
      window.__megaInstallPrompt = null;
      setCanInstall(false);
      return choice;
    } catch (_e) {
      return { outcome: "error" };
    }
  }, []);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  // Detect iPhone/iPod AND modern iPadOS Safari (reports desktop-class "MacIntel" UA with touch)
  const isIpadOS = typeof navigator !== "undefined" && navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  const isIos = (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) || isIpadOS;

  return { installed, canInstall, isIos, promptInstall };
}
